'use client';

import { forwardRef, useMemo } from 'react';

import { useLocalization } from '../../theme';

import { UzNumberInput, type UzNumberInputProps } from './UzNumberInput';

/**
 * Money-aware input.
 *
 * - Currency comes from app config by default (falls back to INR via AppConfig).
 * - Always 4-decimal precision matching how money is stored in Postgres.
 * - Currency symbol auto-resolved from Intl for the active locale.
 */
export interface UzMoneyInputProps extends Omit<UzNumberInputProps, 'precision' | 'leftAdornment'> {
  /** ISO 4217 code. Defaults to localization.defaultCurrency from AppConfig. */
  currency?: string;
}

export const UzMoneyInput = forwardRef<HTMLInputElement, UzMoneyInputProps>(
  ({ currency, ...rest }, ref) => {
    const localization = useLocalization();
    const code = currency ?? localization.defaultCurrency;

    const symbol = useMemo(() => {
      try {
        const parts = new Intl.NumberFormat(localization.defaultLocale, {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol',
        }).formatToParts(0);
        return parts.find((p) => p.type === 'currency')?.value ?? code;
      } catch {
        return code;
      }
    }, [code, localization.defaultLocale]);

    return (
      <UzNumberInput
        ref={ref}
        precision={4}
        leftAdornment={<span className="text-sm tabular-nums">{symbol}</span>}
        {...rest}
      />
    );
  },
);
UzMoneyInput.displayName = 'UzMoneyInput';
